import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import { Check, CornerDownLeft, Link, Pencil, Send, Trash2, X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  useTaskComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
} from "@/features/tasks/hooks/useComments";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import type { MentionSuggestion } from "@/components/ui/RichTextEditor";
import { commentsApi } from "@/features/tasks/api/comments";
import { getRoleLabel } from "@/utils/roleLabel";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import type { TaskComment } from "@/features/tasks/types";

interface Props {
  taskId: string;
  targetCommentId?: string | null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

type CommentNodeType = TaskComment & { replies: CommentNodeType[] };

function buildCommentTree(comments: TaskComment[]): CommentNodeType[] {
  const map = new Map<string, CommentNodeType>();
  comments.forEach((c) => map.set(c.id, { ...c, replies: [] }));
  const roots: CommentNodeType[] = [];
  map.forEach((node) => {
    if (node.reply_to && map.has(node.reply_to)) {
      map.get(node.reply_to)!.replies.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function stripHtml(html: string): string {
  if (!isHtmlContent(html)) return html;
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function CommentContent({ content }: { content: string }) {
  const { data: allUsers = [] } = useInternalUsers({ includeFridayContractors: true });
  const names = allUsers.map((u) => u.full_name);
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-700 dark:text-gray-300 break-words"
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
      />
    );
  }
  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

const MAX_DEPTH = 4;

interface CommentNodeProps {
  node: CommentNodeType;
  depth: number;
  currentUserId: string | null;
  taskId: string;
  editingId: string | null;
  editContent: string;
  editMentionIds: string[];
  mentionSuggestions: MentionSuggestion[];
  onEditStart: (id: string, content: string, mentionIds: string[]) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  onEditContentChange: (content: string) => void;
  onEditMentionIdsChange: (ids: string[]) => void;
  onDelete: (id: string) => void;
  onReply: (c: { id: string; author: string | null; author_name: string; content: string }) => void;
  onCopyLink: (commentId: string) => void;
  copiedId: string | null;
}

function CommentNode({
  node,
  depth,
  currentUserId,
  taskId,
  editingId,
  editContent,
  editMentionIds,
  mentionSuggestions,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditContentChange,
  onEditMentionIdsChange,
  onDelete,
  onReply,
  onCopyLink,
  copiedId,
}: CommentNodeProps) {
  const isOwn = currentUserId === node.author;
  const isEditing = editingId === node.id;
  const visualDepth = Math.min(depth, MAX_DEPTH);

  return (
    <div id={`comment-${node.id}`} className={visualDepth > 0 ? "pl-3 border-l-2 border-gray-200 dark:border-gray-600" : ""}>
      <div className="flex gap-2.5 group py-1.5">
        <UserAvatar
          userId={node.author}
          fullName={node.author_name}
          profilePictureUrl={node.author_profile_picture_url}
          size="lg"
          className="mt-0.5 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{node.author_name}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">{getRoleLabel(node.author_role, node.author_is_ceo)}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{formatDate(node.created_at)}</span>
            {node.is_edited && <span className="text-xs text-gray-400 dark:text-gray-500 italic">(edytowano)</span>}
          </div>

          {node.reply_to_author_name && !isEditing && (
            <div className="flex items-start gap-1.5 mb-1.5 bg-gray-100 dark:bg-gray-700 border-l-2 border-gray-400 dark:border-gray-500 rounded-r px-2 py-1">
              <CornerDownLeft size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{node.reply_to_author_name}: </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 italic truncate">{stripHtml(node.reply_to_content ?? "")}</span>
              </div>
            </div>
          )}

          {isEditing ? (
            <div className="flex flex-col gap-2 mt-1">
              <RichTextEditor
                value={editContent}
                onChange={onEditContentChange}
                placeholder="Edytuj komentarz…"
                onImageUpload={(file) => commentsApi.uploadCommentImage(taskId, file)}
                mentionSuggestions={mentionSuggestions}
                onMentionIdsChange={onEditMentionIdsChange}
              />
              <div className="flex gap-1">
                <button
                  onClick={onEditSave}
                  title="Zapisz"
                  className="p-2 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40"
                >
                  <Check size={15} />
                </button>
                <button
                  onClick={onEditCancel}
                  title="Anuluj"
                  className="p-2 border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
          ) : (
            <CommentContent content={node.content} />
          )}
        </div>

        {!isEditing && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 flex-shrink-0">
            <button
              onClick={() => onCopyLink(node.id)}
              title="Kopiuj link do komentarza"
              className={`${copiedId === node.id ? "text-green-500" : "text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"}`}
            >
              <Link size={13} />
            </button>
            <button
              onClick={() => onReply({ id: node.id, author: node.author, author_name: node.author_name, content: node.content })}
              title="Odpowiedz"
              className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
            >
              <CornerDownLeft size={13} />
            </button>
            {isOwn && (
              <>
                <button
                  onClick={() => onEditStart(node.id, node.content, node.mentions.map((m) => m.id))}
                  className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                >
                  <Pencil size={13} />
                </button>
                <button onClick={() => onDelete(node.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500">
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {node.replies.map((child) => (
        <CommentNode
          key={child.id}
          node={child}
          depth={depth + 1}
          currentUserId={currentUserId}
          taskId={taskId}
          editingId={editingId}
          editContent={editContent}
          editMentionIds={editMentionIds}
          mentionSuggestions={mentionSuggestions}
          onEditStart={onEditStart}
          onEditCancel={onEditCancel}
          onEditSave={onEditSave}
          onEditContentChange={onEditContentChange}
          onEditMentionIdsChange={onEditMentionIdsChange}
          onDelete={onDelete}
          onReply={onReply}
          onCopyLink={onCopyLink}
          copiedId={copiedId}
        />
      ))}
    </div>
  );
}


export function TaskCommentThread({ taskId, targetCommentId }: Props) {
  const { data: comments = [], isLoading } = useTaskComments(taskId);
  const { mutate: createComment, isPending: sending } = useCreateComment(taskId);
  const { mutate: updateComment } = useUpdateComment(taskId);
  const { mutate: deleteComment } = useDeleteComment(taskId);
  const currentUser = useAuthStore((s) => s.user);
  const { data: internalUsers = [] } = useInternalUsers({ includeFridayContractors: true });
  const scrolledRef = useRef(false);

  const mentionSuggestions: MentionSuggestion[] = internalUsers.map((u) => ({
    id: u.id,
    name: u.full_name,
    role: u.role,
    is_ceo: u.is_ceo,
  }));

  const [draft, setDraft, clearDraft] = useCommentDraft(`comment-draft-task-${taskId}`);
  const [composerMentionIds, setComposerMentionIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editMentionIds, setEditMentionIds] = useState<string[]>([]);
  const [replyTo, setReplyTo] = useState<{ id: string; author_name: string; content: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!targetCommentId || isLoading || scrolledRef.current) return;
    const el = document.getElementById(`comment-${targetCommentId}`);
    if (!el) return;
    scrolledRef.current = true;
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("comment-highlight");
      setTimeout(() => el.classList.remove("comment-highlight"), 2000);
    }, 100);
  }, [targetCommentId, isLoading, comments]);

  function handleCopyLink(commentId: string) {
    const url = `${window.location.origin}/tasks/board?task=${taskId}&section=comments&comment=${commentId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(commentId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  function handleReply(c: { id: string; author: string | null; author_name: string; content: string }) {
    if (!c.author) return;
    setReplyTo({ id: c.id, author_name: c.author_name, content: c.content });
  }

  function cancelReply() {
    setReplyTo(null);
  }

  function handleSend() {
    const content = draft.trim();
    if (!content) return;
    createComment(
      { content, mentionIds: composerMentionIds, replyTo: replyTo?.id },
      {
        onSuccess: () => {
          clearDraft();
          setComposerMentionIds([]);
          setReplyTo(null);
        },
      }
    );
  }

  function submitEdit() {
    if (!editingId) return;
    updateComment(
      { id: editingId, content: editContent.trim(), mentionIds: editMentionIds },
      { onSuccess: () => { setEditingId(null); setEditContent(""); setEditMentionIds([]); } }
    );
  }

  if (isLoading) return <div className="flex justify-center py-4"><Spinner size="sm" /></div>;

  return (
    <div className="flex flex-col gap-1">
      {comments.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak komentarzy.</p>
      )}

      {buildCommentTree(comments).map((node) => (
        <CommentNode
          key={node.id}
          node={node}
          depth={0}
          currentUserId={currentUser?.id ?? null}
          taskId={taskId}
          editingId={editingId}
          editContent={editContent}
          editMentionIds={editMentionIds}
          mentionSuggestions={mentionSuggestions}
          onEditStart={(id, content, mentionIds) => { setEditingId(id); setEditContent(content); setEditMentionIds(mentionIds); }}
          onEditCancel={() => { setEditingId(null); setEditContent(""); setEditMentionIds([]); }}
          onEditSave={submitEdit}
          onEditContentChange={setEditContent}
          onEditMentionIdsChange={setEditMentionIds}
          onDelete={(id) => deleteComment(id)}
          onReply={handleReply}
          onCopyLink={handleCopyLink}
          copiedId={copiedId}
        />
      ))}

      {/* Composer */}
      <div className="flex flex-col mt-2 border-t border-gray-100 pt-3">
        {replyTo && (
          <div className="flex items-start gap-2 mb-2 bg-gray-100 dark:bg-gray-700 border-l-2 border-gray-400 dark:border-gray-500 rounded-r-lg px-3 py-1.5">
            <CornerDownLeft size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{replyTo.author_name}</span>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{stripHtml(replyTo.content)}</p>
            </div>
            <button onClick={cancelReply} className="text-gray-400 hover:text-red-400 flex-shrink-0">
              <X size={12} />
            </button>
          </div>
        )}
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          disabled={sending}
          placeholder="Napisz komentarz… (wpisz @ aby oznaczyć)"
          onImageUpload={(file) => commentsApi.uploadCommentImage(taskId, file)}
          mentionSuggestions={mentionSuggestions}
          onMentionIdsChange={setComposerMentionIds}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            className="p-2 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
